import { FC, PropsWithChildren, useMemo } from 'react';
import { cn } from '@/utils/classNames.ts';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { useTranslation } from 'react-i18next';
import AvailabilityBadge from '@/components/ui/AvailabilityBadge/AvailabilityBadge.tsx';
import type { Availability as AvailabilityT } from '@/_constants/Availability.ts';
import { Button, Tooltip } from 'react-daisyui';
import { PaperClipIcon, TrashIcon } from '@heroicons/react/24/outline';

type SearchResultProps = {
  phrase: string;
  tld_pref?: string | null;
  domains: { name: string; availability: Omit<AvailabilityT, 'loading'> }[];
  socialMedia: { name: string; availability: Omit<AvailabilityT, 'loading'> }[];
  onPinClick?: () => void;
  isPinned?: boolean;
};

const SearchResult: FC<PropsWithClassName<SearchResultProps>> = ({
  className,
  phrase,
  tld_pref,
  domains,
  socialMedia,
  onPinClick,
  isPinned,
}) => {
  const { t } = useTranslation();

  const tldPref = tld_pref ? tld_pref.replace(/\s+/g, '') : null;

  const prefDomain = useMemo(
    () => (tldPref ? domains.find((domain) => domain.name.endsWith(tldPref)) : null),
    [domains, tldPref]
  );
  const restDomains = useMemo(
    () => (prefDomain ? domains.filter((domain) => domain.name !== prefDomain.name) : domains),
    [domains, prefDomain]
  );

  return (
    <Wrapper className={className}>
      {onPinClick ? (
        <Tooltip
          message={!isPinned ? t('button.pin_results') : t('button.unpin_results')}
          position="left"
          className="absolute -top-4 -right-4 w-fit"
        >
          <Button
            size="sm"
            color={!isPinned ? 'accent' : 'error'}
            shape="circle"
            className="drop-shadow-lg"
            onClick={onPinClick}
            aria-label={!isPinned ? t('button.pin_results') : t('button.unpin_results')}
          >
            {!isPinned ? <PaperClipIcon className="w-5 h-5" /> : <TrashIcon className="w-5 h-5" />}
          </Button>
        </Tooltip>
      ) : null}
      <Section>
        <Label>{t('label.phrase')}:</Label>
        <AvailabilityBadge content={phrase} className="min-w-28 justify-center" />
      </Section>
      {prefDomain ? (
        <Section>
          <Label>{t('label.pref_domain')}:</Label>
          <AvailabilityBadge
            content={prefDomain.name}
            type={prefDomain.availability}
            className="min-w-28 justify-center"
          />
        </Section>
      ) : null}
      <Section>
        <Label>{t('label.domains')}:</Label>
        <div className="flex flex-wrap gap-3 w-full">
          {restDomains.map((domain) => (
            <Tooltip
              className="w-fit"
              message={t(`label.availability_info.${domain.availability}`)}
              key={`tooltip_domain_${domain.name}`}
            >
              <AvailabilityBadge className="w-fit max-w-full" type={domain.availability} content={domain.name} />
            </Tooltip>
          ))}
        </div>
      </Section>
      <Section>
        <Label>{t('label.social_media')}:</Label>
        <div className="flex flex-wrap gap-3">
          {socialMedia.map((media) => (
            <Tooltip
              className="w-fit"
              message={t(`label.availability_info.${media.availability}`)}
              key={`tooltip_domain_${media.name}`}
            >
              <AvailabilityBadge
                key={`media_${media.name}`}
                className="w-fit max-w-full"
                type={media.availability}
                content={media.name}
              />
            </Tooltip>
          ))}
        </div>
      </Section>
    </Wrapper>
  );
};

export default SearchResult;

function Wrapper({ children, className }: PropsWithChildren<PropsWithClassName>) {
  return (
    <div
      className={cn(
        'border border-gray-100 rounded-2xl px-6 flex flex-col divide-y divide-gray-100 bg-white relative',
        className
      )}
    >
      {children}
    </div>
  );
}

function Label({ children }: PropsWithChildren) {
  return <div className="text-sm">{children}</div>;
}

function Section({ children }: PropsWithChildren) {
  return <div className="py-6 flex flex-col items-start gap-2">{children}</div>;
}

export function NoResults({ children, className }: PropsWithChildren<PropsWithClassName>) {
  return (
    <Wrapper className={className}>
      <Section>
        <Label>{children}</Label>
      </Section>
    </Wrapper>
  );
}
